import type { ReactNode } from "react";
import { Icon, iconMap } from "../Icon";
import styles from "./Button.module.css";
import type { ButtonProps, ButtonSize, ButtonState, ButtonStyle, ButtonType } from "./types";

const styleClassMap: Record<ButtonStyle, string> = {
  primary: styles.primary,
  outline: styles.outline,
  text: styles.text,
  chip: styles.chip,
  warn: styles.warn,
};

const sizeClassMap: Record<ButtonSize, string> = {
  small: styles.small,
  large: styles.large,
};

const stateClassMap: Record<ButtonState, string> = {
  default: styles.default,
  hover: styles.hover,
  pressed: styles.pressed,
  disabled: styles.disabled,
};

const typeClassMap: Record<ButtonType, string> = {
  text: styles.textButton,
  icon: styles.iconButton,
};

type ButtonIconName = NonNullable<ButtonProps["iconName"]>;

function resolveIconName(type: ButtonType, iconName?: ButtonIconName): ButtonIconName {
  if (iconName) {
    return iconName;
  }

  return type === "icon" ? "edit" : "plus";
}

function resolveIconSize(size: ButtonSize): "s" | "m" {
  return size === "large" ? "m" : "s";
}

function resolveIconStyle(style: ButtonStyle, iconName: ButtonIconName) {
  if (style !== "chip") {
    return "outline" as const;
  }

  return iconMap[iconName]?.fill ? "fill" : "outline";
}

export function Button({
  ariaLabel,
  children,
  className,
  htmlType = "button",
  iconName,
  iconSize,
  iconStyleType,
  leftIcon = true,
  onClick,
  rightIcon = true,
  size = "large",
  state = "default",
  style = "primary",
  type = "text",
}: ButtonProps) {
  const resolvedIconName = resolveIconName(type, iconName);
  const resolvedIconSize = iconSize ?? resolveIconSize(size);
  const resolvedIconStyle = iconStyleType ?? resolveIconStyle(style, resolvedIconName);
  const isDisabled = state === "disabled";
  const showLeftIcon = type === "text" && leftIcon;
  const showRightIcon = type === "text" && rightIcon;
  const iconOnly = type === "icon";
  const label: ReactNode = children ?? "Add new";

  return (
    <button
      type={htmlType}
      disabled={isDisabled}
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={[
        styles.button,
        styleClassMap[style],
        sizeClassMap[size],
        stateClassMap[state],
        typeClassMap[type],
        className ?? "",
      ].join(" ")}
    >
      {iconOnly ? (
        <span className={styles.iconSlot}>
          <Icon name={resolvedIconName} size={resolvedIconSize} styleType={resolvedIconStyle} />
        </span>
      ) : (
        <>
          {showLeftIcon ? (
            <span className={styles.iconSlot}>
              <Icon name={resolvedIconName} size={resolvedIconSize} styleType={resolvedIconStyle} />
            </span>
          ) : null}
          <span className={styles.label}>{label}</span>
          {showRightIcon ? (
            <span className={styles.iconSlot}>
              <Icon name={resolvedIconName} size={resolvedIconSize} styleType={resolvedIconStyle} />
            </span>
          ) : null}
        </>
      )}
    </button>
  );
}
